import { CustomLink } from '@/components/custom-link/CustomLink';
import {
  NavbarItemTypeEnum,
  type NavbarItem,
  type NavbarMenuItem,
} from '@/shared/constant/navbar/NavbarItem';
import { isClient } from '@/shared/util/ssr';
import { Dropdown } from 'aui';
import { FoxIconArrowNarrowUpRight, FoxIconDownMd, FoxIconRightMd } from 'aui-biz-icons';
import type { ItemType } from 'aui/lib/menu/hooks/useItems';
import clsx from 'clsx';
import React from 'react';
import type { NavbarGroupTypeLevel } from '../../type/NavbarGroupTypeLevel';
import { IconLabel } from '../icon-label/IconLabel';
import { NavbarElement } from '../navbar-element/NavbarElement';
import styles from './NavbarMenuElement.module.css';

export type NavbarMenuElementProps = {
  groupLevelType: NavbarGroupTypeLevel;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  subfix?: React.ReactNode;
  className?: string;
  value: NavbarMenuItem;
};

export function NavbarMenuElement(props: NavbarMenuElementProps) {
  const { value, onClick, className, groupLevelType } = props;

  const [isOpen, setIsOpen] = React.useState(false);
  const [showMenuChild, setShowMenuChild] = React.useState(true);

  const navbarItemToItemType = (x: NavbarItem): ItemType => {
    const selected = x.type === NavbarItemTypeEnum.Link ? false : x.selected;
    const isSameOrigin =
      x.type === NavbarItemTypeEnum.Link && isClient && x.to.startsWith(window.location.origin);
    const target = (() => {
      if (x.type === NavbarItemTypeEnum.Link) {
        return x.target;
      }
      return undefined;
    })();
    const subfixIcon = (() => {
      if (x.type === NavbarItemTypeEnum.Link && !isSameOrigin) {
        return <FoxIconArrowNarrowUpRight size={12} className="text-disabled" />;
      }

      return undefined;
    })();
    const to = (() => {
      if (x.type === NavbarItemTypeEnum.Menu) {
        return '';
      }
      return x.to;
    })();
    const disabled = (() => {
      if (x.type === NavbarItemTypeEnum.Menu) {
        return x.disabled || !x.children.length;
      }

      return x.disabled || !to;
    })();

    return {
      key: x.id,
      disabled,
      children:
        x.type === NavbarItemTypeEnum.Menu
          ? x.children.map((y) => navbarItemToItemType(y))
          : undefined,
      title: x.title,
      label: (
        <CustomLink
          to={to}
          target={target}
          disabled={x.disabled}
          onClick={(e) => {
            setIsOpen(false);
            onClick?.(e);
          }}
        >
          <IconLabel
            className="w-[192px]"
            icon={x.icon}
            label={x.title}
            selected={selected}
            subfix={subfixIcon}
          />
        </CustomLink>
      ),
    };
  };

  return (
    <Dropdown
      open={isOpen}
      onOpenChange={setIsOpen}
      overlayClassName={clsx('block max-os:hidden', styles.dropdown)}
      destroyPopupOnHide
      menu={{
        items: value.children.map((x) => navbarItemToItemType(x)),
      }}
    >
      <div
        className={clsx('flex-shrink-0 cursor-pointer max-os:w-full', className)}
        title={value.title}
      >
        <NavbarElement
          value={{
            ...value,
            type: NavbarItemTypeEnum.Folder,
            to: '',
          }}
          groupLevelType={groupLevelType}
          subfix={
            <>
              <FoxIconDownMd className="hidden os:inline-block" size={16} />
              {showMenuChild ? (
                <FoxIconDownMd className="inline-block os:hidden" size={16} />
              ) : (
                <FoxIconRightMd className="inline-block os:hidden" size={16} />
              )}
            </>
          }
          onClick={() => {
            setShowMenuChild(!showMenuChild);
          }}
        />

        <ul
          className={clsx('hidden pl-6', {
            ['max-os:block']: showMenuChild,
          })}
        >
          {value.children.map((x) => {
            if (x.type === NavbarItemTypeEnum.Menu) {
              return null;
            }
            return (
              <li key={x.id}>
                <NavbarElement groupLevelType={groupLevelType} value={x} noBold onClick={onClick} />
              </li>
            );
          })}
        </ul>
      </div>
    </Dropdown>
  );
}
